import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qAvailableBatchesForDispense,
  qSupplyItems,
  qUsedSuppliesForEncounter,
  type PharmacyBatchOptionRow,
  type SupplyPickerRow,
  type UsedSupplyRow,
} from '@helios/data-access';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { NavIcon, RightDrawer } from '../../../shell/shared';
import { createUsedSupply, reverseUsedSupply } from '../../../services/used-supply-service';

// PRD.264 — Used Supplies drawer. Records supplies used during a visit. Usage
// deducts stock but never bills; Billing later pulls ready-for-billing rows.
// Batch selection stays here (a simple inline dropdown) — never in the package
// or invoice flows.

interface Props {
  open: boolean;
  onClose: () => void;
  patientId: string;
  encounterId: string;
  visitId: string | null;
}

export function UsedSuppliesPanel({ open, onClose, patientId, encounterId, visitId }: Props) {
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const roles = orgId ? (claims?.orgs?.[orgId]?.roles ?? []) : [];
  const isAdmin = roles.includes('admin');
  const userId = user?.uid ?? null;
  const { db } = usePowerSyncDb();

  const { data: usedRows = [] } = usePowerSyncQuery<UsedSupplyRow>(
    qUsedSuppliesForEncounter(encounterId),
  );

  return (
    <RightDrawer open={open} onClose={onClose} title="Used Supplies" icon="package">
      <p
        style={{
          margin: '0 0 var(--sp-3)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-muted)',
        }}
      >
        Record supplies used during this visit. Stock is deducted when you add a supply; billing is
        separate.
      </p>

      <AddSupplyForm
        orgId={orgId}
        patientId={patientId}
        encounterId={encounterId}
        visitId={visitId}
        userId={userId}
        db={db}
      />

      <div
        style={{
          borderTop: '1px solid var(--border)',
          marginTop: 'var(--sp-4)',
          paddingTop: 'var(--sp-3)',
        }}
      >
        <SectionTitle>Used Supplies for this Visit</SectionTitle>
        {usedRows.length === 0 ? (
          <div
            style={{
              padding: 'var(--sp-4)',
              border: '1px dashed var(--border)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              textAlign: 'center',
            }}
          >
            No supplies recorded yet. Add supplies used during this visit.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {usedRows.map((r) => (
              <UsedSupplyRowView key={r.id} row={r} isAdmin={isAdmin} db={db} userId={userId} />
            ))}
          </div>
        )}
      </div>
    </RightDrawer>
  );
}

type DbLike = ReturnType<typeof usePowerSyncDb>['db'];

function AddSupplyForm({
  orgId,
  patientId,
  encounterId,
  visitId,
  userId,
  db,
}: {
  orgId: string;
  patientId: string;
  encounterId: string;
  visitId: string | null;
  userId: string | null;
  db: DbLike;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const searchWrapRef = useRef<HTMLDivElement | null>(null);
  const [selected, setSelected] = useState<SupplyPickerRow | null>(null);
  const [qty, setQty] = useState('1');
  const [batchId, setBatchId] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(search), 200);
    return () => window.clearTimeout(t);
  }, [search]);

  // Dismiss the supply search dropdown on outside-click or Escape, like the
  // other inventory pickers.
  useEffect(() => {
    if (!listOpen) return;
    function onDoc(e: MouseEvent) {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target as Node)) {
        setListOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setListOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [listOpen]);

  const { data: candidates = [] } = usePowerSyncQuery<SupplyPickerRow>(
    qSupplyItems(orgId, debounced),
  );
  const { data: batches = [] } = usePowerSyncQuery<PharmacyBatchOptionRow>(
    qAvailableBatchesForDispense(selected?.id ?? ''),
  );
  const batchTracked = selected?.batch_tracked === 1;

  const reset = () => {
    setSelected(null);
    setSearch('');
    setDebounced('');
    setListOpen(false);
    setQty('1');
    setBatchId('');
    setNotes('');
    setError(null);
  };

  const submit = async () => {
    if (!db || !selected || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await createUsedSupply({
        db,
        userId,
        input: {
          patientId,
          encounterId,
          visitId,
          inventoryItemId: selected.id,
          inventoryBatchId: batchTracked ? batchId || null : null,
          quantity: Number(qty),
          notes: notes.trim() || null,
        },
      });
      if (!res.ok) setError(res.error ?? 'Could not record used supply.');
      else {
        reset();
        setOpen(false);
      }
    } catch {
      setError('Could not record used supply. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} style={primaryBtn}>
        <NavIcon name="plus" /> Add Supply
      </button>
    );
  }

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-surface-2, #f8fafc)',
        padding: 'var(--sp-3)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
      }}
    >
      <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semi)' }}>
        Add Supply
      </span>

      {selected ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
            {selected.item_name}
            <span
              style={{ color: 'var(--text-muted)', marginLeft: 6, fontSize: 'var(--font-size-xs)' }}
            >
              {kindLabel(selected.supply_usage_type)}
            </span>
          </span>
          <button type="button" onClick={() => setSelected(null)} style={linkBtn}>
            Change
          </button>
        </div>
      ) : (
        <div ref={searchWrapRef} style={{ position: 'relative' }}>
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setListOpen(true);
            }}
            onFocus={() => setListOpen(true)}
            placeholder="Search supply by name or code"
            style={inputStyle}
          />
          {listOpen && (search.trim() !== '' || candidates.length > 0) && (
            <div style={dropdownStyle}>
              {candidates.length === 0 ? (
                <div
                  style={{
                    padding: 'var(--sp-2) var(--sp-3)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                  }}
                >
                  No supplies found.
                </div>
              ) : (
                candidates.slice(0, 20).map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      setSelected(c);
                      setBatchId('');
                      setListOpen(false);
                    }}
                    style={dropdownItem}
                  >
                    <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
                      {c.item_name}
                    </span>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                      {kindLabel(c.supply_usage_type)} {c.sku ? `· ${c.sku}` : ''}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>Qty</span>
          <input
            type="number"
            min="0"
            step="any"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            style={{ ...inputStyle, width: 72 }}
          />
        </label>
        {batchTracked && (
          <select
            value={batchId}
            onChange={(e) => setBatchId(e.target.value)}
            style={{ ...inputStyle, flex: 1 }}
          >
            <option value="">Select batch…</option>
            {batches.map((b) => (
              <option key={b.inventory_batch_id} value={b.inventory_batch_id}>
                {b.batch_number ?? 'Batch'}
                {b.expiry_date ? ` · Exp ${b.expiry_date}` : ''} · {b.available_quantity ?? 0} avail
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Proactive hint: batch-tracked supply with no batches to pick. */}
      {batchTracked && selected && batches.length === 0 && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
          }}
        >
          No batches in stock for this supply.
          <AddStockHelpTip />
        </span>
      )}

      <input
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes (optional)"
        style={inputStyle}
      />

      {error && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-danger, #dc2626)',
          }}
        >
          {error}
          {/^select a batch/i.test(error) && <AddStockHelpTip />}
        </span>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
        <button
          type="button"
          onClick={() => {
            reset();
            setOpen(false);
          }}
          disabled={busy}
          style={ghostBtn}
        >
          Cancel
        </button>
        <button type="button" onClick={submit} disabled={busy || !selected} style={primaryBtnSmall}>
          {busy ? 'Adding…' : 'Add'}
        </button>
      </div>
    </div>
  );
}

function UsedSupplyRowView({
  row,
  isAdmin,
  db,
  userId,
}: {
  row: UsedSupplyRow;
  isAdmin: boolean;
  db: DbLike;
  userId: string | null;
}) {
  const [reversing, setReversing] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reversed = row.usage_status === 'reversed';
  const billing = billingMeta(row.billing_status);

  const doReverse = async () => {
    if (!db || busy || !reason.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await reverseUsedSupply({ db, usedSupplyId: row.id, reason, userId });
      if (!res.ok) setError(res.error ?? 'Could not reverse.');
      else {
        setReversing(false);
        setReason('');
      }
    } catch {
      setError('Could not reverse. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: 'var(--sp-2) 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-2)' }}>
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text)',
            textDecoration: reversed ? 'line-through' : 'none',
          }}
        >
          {row.item_name_snapshot ?? 'Supply'}{' '}
          <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>
            {kindLabel(row.supply_usage_type_snapshot)}
          </span>
        </span>
        {reversed ? (
          <Pill label="Reversed" bg="var(--bg-surface-2)" color="var(--text-muted)" />
        ) : (
          <Pill label={billing.label} bg={billing.bg} color={billing.color} />
        )}
      </div>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
        Qty {fmtQty(row.quantity)} {row.unit_snapshot ?? ''}
        {row.batch_no_snapshot ? ` · Batch ${row.batch_no_snapshot}` : ''}
        {row.expiry_date_snapshot ? ` · Exp ${row.expiry_date_snapshot}` : ''}
        {row.used_at ? ` · ${row.used_at.slice(0, 10)}` : ''}
      </div>
      {isAdmin && !reversed && (
        <div style={{ marginTop: 4 }}>
          {reversing ? (
            <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason (required)"
                style={{ ...inputStyle, flex: 1 }}
              />
              <button
                type="button"
                disabled={busy || !reason.trim()}
                onClick={doReverse}
                style={primaryBtnSmall}
              >
                Confirm
              </button>
            </div>
          ) : (
            <button type="button" onClick={() => setReversing(true)} style={linkBtn}>
              Reverse Used Supply
            </button>
          )}
          {error && (
            <span
              style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger, #dc2626)' }}
            >
              {error}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── bits ───

// PRD.264 — a "?" that explains how to make batches/stock available. The
// popover is portalled to <body> with fixed positioning so it isn't clipped by
// the drawer's scroll container. Links open in a new tab so the encounter +
// drawer stay open.
function AddStockHelpTip() {
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const toggle = () => {
    if (pos) {
      setPos(null);
      return;
    }
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = 240;
    // Anchor the popover's right edge near the "?", clamped to the viewport.
    const left = Math.min(Math.max(8, r.right - width), window.innerWidth - width - 8);
    setPos({ top: r.bottom + 6, left });
  };

  return (
    <span style={{ display: 'inline-flex' }}>
      <button
        ref={btnRef}
        type="button"
        aria-label="How to add stock"
        title="How to add stock"
        onClick={toggle}
        style={{
          width: 15,
          height: 15,
          lineHeight: '13px',
          borderRadius: '50%',
          border: '1px solid var(--border)',
          background: 'var(--bg-surface)',
          color: 'var(--text-muted)',
          fontSize: 10,
          fontWeight: 700,
          cursor: 'pointer',
          padding: 0,
          textAlign: 'center',
        }}
      >
        ?
      </button>
      {pos &&
        createPortal(
          <>
            {/* Click-away backdrop. */}
            <div
              onClick={() => setPos(null)}
              style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
            />
            <div
              style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                zIndex: 9999,
                width: 240,
                background: 'var(--bg-surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
                padding: 'var(--sp-3)',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                Add stock (and batches) for this supply:
              </span>
              <a
                href="/inventory/delivery-orders"
                target="_blank"
                rel="noreferrer"
                style={helpLink}
              >
                <NavIcon name="folder-open" /> Create a Delivery Order
              </a>
              <a
                href="/inventory/adjustments/new"
                target="_blank"
                rel="noreferrer"
                style={helpLink}
              >
                <NavIcon name="edit" /> Create an Inventory Adjustment
              </a>
            </div>
          </>,
          document.body,
        )}
    </span>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-semi)',
        color: 'var(--text)',
        margin: '0 0 var(--sp-2)',
      }}
    >
      {children}
    </div>
  );
}

function Pill({ label, bg, color }: { label: string; bg: string; color: string }) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: 'var(--radius-full, 9999px)',
        background: bg,
        color,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        whiteSpace: 'nowrap',
        alignSelf: 'flex-start',
      }}
    >
      {label}
    </span>
  );
}

function kindLabel(k: string | null): string {
  if (k === 'consumable') return '· Consumable';
  if (k === 'expendable') return '· Expendable';
  return '· Supply';
}

function billingMeta(status: string | null): { label: string; bg: string; color: string } {
  switch (status) {
    case 'ready_for_billing':
      return {
        label: 'Ready for Billing',
        bg: 'var(--color-info-bg, #dbeafe)',
        color: 'var(--color-info-text, #1e40af)',
      };
    case 'billed':
      return { label: 'Billed', bg: 'var(--color-success-bg)', color: 'var(--color-success-text)' };
    default:
      return { label: 'Internal use', bg: 'var(--bg-surface-2)', color: 'var(--text-muted)' };
  }
}

function fmtQty(n: number | null): string {
  if (n === null || n === undefined) return '0';
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(3)));
}

const inputStyle: CSSProperties = {
  height: 32,
  padding: '0 8px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  fontFamily: 'var(--font-family)',
  width: '100%',
  boxSizing: 'border-box',
};

const dropdownStyle: CSSProperties = {
  position: 'absolute',
  top: 36,
  left: 0,
  right: 0,
  zIndex: 30,
  maxHeight: 240,
  overflowY: 'auto',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-lg, 0 8px 24px rgba(0,0,0,0.12))',
};

const dropdownItem: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  width: '100%',
  padding: 'var(--sp-2) var(--sp-3)',
  border: 'none',
  borderBottom: '1px solid var(--border)',
  background: 'transparent',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'var(--font-family)',
};

const primaryBtn: CSSProperties = {
  height: 34,
  padding: '0 12px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-primary)',
  background: 'var(--bg-surface)',
  color: 'var(--color-primary)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semi)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
};

const primaryBtnSmall: CSSProperties = {
  padding: '4px 12px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--color-primary)',
  background: 'var(--color-primary)',
  color: '#fff',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const ghostBtn: CSSProperties = {
  padding: '4px 12px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--text-muted)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-medium)',
  cursor: 'pointer',
};

const linkBtn: CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: 'var(--color-primary)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  cursor: 'pointer',
  padding: 0,
};

const helpLink: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  color: 'var(--color-primary)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  textDecoration: 'none',
};
